import React, { useState } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { Layout, Menu, Typography, Select, Tooltip, theme } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import MDXLoader from './MDXLoader';
import type { SupportedLanguage } from '../i18n';

const { Content, Sider } = Layout;
const { Title } = Typography;
const { Option } = Select;

interface DocItem {
  key: string;
  labelKey: string;
  path: string;
}

const documentationItems: DocItem[] = [
  {
    key: 'getting-started',
    labelKey: 'documentation.menu.gettingStarted',
    path: 'getting-started',
  },
  {
    key: 'tutorial',
    labelKey: 'documentation.menu.interactiveTutorial',
    path: 'tutorial',
  },
  {
    key: 'features',
    labelKey: 'documentation.menu.features',
    path: 'features',
  },
  {
    key: 'components',
    labelKey: 'documentation.menu.components',
    path: 'components',
  },
  {
    key: 'api',
    labelKey: 'documentation.menu.apiReference',
    path: 'api',
  },
];

interface DocumentationProps {
  initialPage?: string;
}

const Documentation: React.FC<DocumentationProps> = ({ initialPage = 'getting-started' }) => {
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation();
  const [selectedKey, setSelectedKey] = useState<string>(initialPage);
  const [docLanguage, setDocLanguage] = useState<SupportedLanguage>(i18n.language as SupportedLanguage || 'en');

  // Update selected page when initialPage prop changes
  React.useEffect(() => {
    setSelectedKey(initialPage);
  }, [initialPage]);

  const handleMenuClick = (key: string) => {
    setSelectedKey(key);
  };

  const handleLanguageChange = (value: unknown) => {
    setDocLanguage(value as SupportedLanguage);
  };

  const availableLanguages: SupportedLanguage[] = ['en', 'nl', 'fr', 'de'];

  return (
    <Layout style={{ height: '100%' }}>
      <Sider 
        width={250}
        style={{
          borderRight: '1px solid',
          borderColor: token.colorBorder,
          backgroundColor: token.colorBgContainer,
          overflow: 'auto',
        }}
      >
        <div style={{ padding: '24px 0 16px 24px', textAlign: 'left' }}>
          <Title level={4} style={{ margin: 0, textAlign: 'left' }}>
            {t('documentation.title')}
          </Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{ height: '100%', borderRight: 0 }}
          items={documentationItems.map(item => ({
            key: item.key,
            label: t(item.labelKey),
            onClick: () => handleMenuClick(item.key),
          }))}
        />
      </Sider>
      <Layout style={{ height: '100%' }}>
        <div 
          style={{ 
            padding: '16px 24px',
            borderBottom: `1px solid ${token.colorBorder}`,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Tooltip title={t('documentation.languageSelector.tooltip')}>
            <Select
              value={docLanguage}
              onChange={handleLanguageChange}
              suffixIcon={<GlobalOutlined />}
              placeholder={t('documentation.languageSelector.label')}
              style={{ width: 140 }}
            >
              {availableLanguages.map(lang => (
                <Option key={lang} value={lang}>
                  {t(`languages.${lang}`)}
                </Option>
              ))}
            </Select>
          </Tooltip>
        </div>
        <Content
          style={{
            padding: '24px',
            margin: 0,
            minHeight: 280,
            overflow: 'auto',
          }}
        >
          <MDXProvider>
            <MDXLoader path={selectedKey} language={docLanguage} />
          </MDXProvider>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Documentation; 